import type { SkillShape } from "@eldrun/content";
import type { DamageEvent } from "@eldrun/protocol";
import type { PieceSpec } from "./skill-pieces.ts";

/**
 * O desenho de uma passiva de classe que agiu num dano (Fase 12, Gate C), por dados sobre as peças e os
 * anéis de sempre: quem dá a cor (`atacante` ou `alvo`), as peças 3D e, se houver, a onda no chão.
 */
export interface PassiveFx {
  passive: "escudo-erguido" | "impeto" | "ressonancia" | "olho-de-falcao";
  color: "atacante" | "alvo";
  pieces: PieceSpec[];
  wave?: { radius: number; shape: SkillShape; ms: number };
}

const TAU = Math.PI * 2;

/**
 * O que desenhar para as passivas de um dano (Fase 12, Gate C), só pelo que o servidor mandou (`kind` e
 * `procs`): sem eles, nada. `x/y/z` é o ponto do golpe (o meio do corpo atingido), `gy` o chão ali e
 * `facing` para onde o alvo olha (o Escudo Erguido sai na frente dele).
 * - Escudo Erguido: três facetas em arco na frente de quem segurou, na cor dele, rápidas.
 * - Ímpeto: triângulos abrindo do ponto do golpe e uma onda de triângulos, na cor de quem bateu.
 * - Ressonância (o Eco): uma onda de círculos e nós subindo no alvo do eco.
 * - Olho de Falcão: a mira (triângulos girando em volta do alvo) e uma onda de linhas.
 */
export function passiveFx(e: Pick<DamageEvent, "kind" | "procs">, x: number, y: number, z: number, gy: number, facing = 0): PassiveFx[] {
  const out: PassiveFx[] = [];
  const blocked = e.procs?.bloqueio ?? 0;
  if (blocked > 0) {
    // De frente (o que o escudo cobre): o `facing` é o yaw do corpo, que olha para −z no yaw 0.
    const pieces = [-0.55, 0, 0.55].map((d): PieceSpec => {
      const a = facing + d;
      return { shape: "faceta", x: x - Math.sin(a) * 0.75, y, z: z - Math.cos(a) * 0.75, scale: 0.55, scaleEnd: 0.8, yaw: a, tilt: Math.PI / 2, ms: 380, glow: 2.4, fill: 0.25 };
    });
    out.push({ passive: "escudo-erguido", color: "alvo", pieces });
  }
  if (e.procs?.impeto) {
    const pieces = Array.from({ length: 4 }, (_, i): PieceSpec => ({ shape: "triangulo", x, y, z, scale: 0.5, scaleEnd: 0.9, orbit: { cx: x, cz: z, r0: 0.2, r1: 1.2, a0: (i / 4) * TAU + 0.4, speed: 0 }, ms: 300, glow: 2.6, fill: 0.2 }));
    out.push({ passive: "impeto", color: "atacante", pieces, wave: { radius: 1.4, shape: "triangulos", ms: 320 } });
  }
  if (e.kind === "eco") {
    const pieces = Array.from({ length: 3 }, (_, i): PieceSpec => {
      const a = (i / 3) * TAU;
      return { shape: "no", x: x + Math.cos(a) * 0.45, y: gy + 0.4, z: z + Math.sin(a) * 0.45, scale: 1, rise: 0.9, ms: 450, glow: 2.4, fill: 0.6 };
    });
    out.push({ passive: "ressonancia", color: "atacante", pieces, wave: { radius: 1.6, shape: "circulos", ms: 420 } });
  }
  if (e.procs?.olho) {
    const pieces = Array.from({ length: 4 }, (_, i): PieceSpec => ({ shape: "triangulo", x, y, z, scale: 0.35, orbit: { cx: x, cz: z, r0: 0.75, r1: 0.55, a0: (i / 4) * TAU, speed: 4 }, ms: 400, glow: 2.4, fill: 0 }));
    out.push({ passive: "olho-de-falcao", color: "atacante", pieces, wave: { radius: 1, shape: "linhas", ms: 360 } });
  }
  return out;
}

/**
 * O traço da Flecha Sinalizadora (Fase 12, Gate C): o servidor aplica a área na hora, sem projétil, então
 * o desenho é um traço instantâneo de quem atirou até o ponto (um prisma esticado), que some rápido. Nada
 * de voo: um projétil desenhado chegaria depois do efeito que já valeu.
 */
export function flareTracer(from: { x: number; y: number; z: number }, to: { x: number; y: number; z: number }): PieceSpec | undefined {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const dz = to.z - from.z;
  const length = Math.hypot(dx, dy, dz);
  if (length < 0.5) return undefined;
  // O prisma deita em z: o yaw o vira no chão e a inclinação (em x, antes do yaw) sobe ou desce.
  return { shape: "prisma", x: (from.x + to.x) / 2, y: (from.y + to.y) / 2, z: (from.z + to.z) / 2, scale: 0.35, length, yaw: Math.atan2(dx, dz), tilt: Math.atan2(-dy, Math.hypot(dx, dz)), ms: 260, glow: 3, fill: 0.5 };
}
